import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    Activity, 
    Trash2, 
    AlertTriangle, 
    Shield, 
    Mail,
    ArrowLeft,
    CheckCircle2,
    XCircle,
    Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeleteAccount() {
    const { isAuthenticated, user, logout } = useAuth();
    const navigate = useNavigate();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);

    const handleDeleteAccount = async () => {
        if (confirmText !== 'DELETE') {
            toast.error('Please type DELETE to confirm');
            return;
        }

        setDeleting(true);
        try {
            await axios.post(`${API}/auth/deactivate`);
            toast.success('Your account has been deleted successfully');
            logout();
            navigate('/');
        } catch (error) {
            console.error('Delete account error:', error);
            toast.error(error.response?.data?.detail || 'Failed to delete account');
        } finally {
            setDeleting(false);
            setShowDeleteDialog(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-background to-orange-50 dark:from-red-950/20 dark:via-background dark:to-orange-950/20">
            {/* Header */}
            <header className="border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Activity className="w-6 h-6 text-primary" />
                        <span className="font-heading font-bold text-lg">MediGuide AI</span>
                    </Link>
                    <Link to="/">
                        <Button variant="ghost" size="sm">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Home
                        </Button>
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 py-12">
                {/* Page Title */}
                <div className="text-center mb-10">
                    <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                        <Trash2 className="w-8 h-8 text-red-600 dark:text-red-400" />
                    </div>
                    <h1 className="text-3xl font-bold text-foreground mb-2">
                        Delete Your Account
                    </h1>
                    <p className="text-muted-foreground max-w-xl mx-auto">
                        Request deletion of your MediGuide account and all associated data
                    </p>
                </div>

                {/* What Gets Deleted */}
                <Card className="mb-8 border-red-200 dark:border-red-800">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
                            <AlertTriangle className="w-5 h-5" />
                            What Gets Deleted
                        </CardTitle>
                        <CardDescription>
                            This action is permanent and cannot be undone
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ul className="space-y-3">
                            {[
                                { text: 'Your account profile and personal information', icon: XCircle },
                                { text: 'All symptom checks and health assessments', icon: XCircle },
                                { text: 'Diagnosis history and medical records', icon: XCircle },
                                { text: 'Shared diagnosis links', icon: XCircle },
                                { text: 'Consultation requests and messages', icon: XCircle },
                                { text: 'Payment history and subscription data', icon: XCircle },
                                { text: 'Health diary entries', icon: XCircle },
                            ].map((item, index) => (
                                <li key={index} className="flex items-start gap-3">
                                    <item.icon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-foreground">{item.text}</span>
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>

                {/* How to Delete */}
                <Card className="mb-8">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-primary" />
                            How to Delete Your Account
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {isAuthenticated ? (
                            /* Logged in - Direct deletion */
                            <div className="space-y-4">
                                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                    <p className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium">
                                        <CheckCircle2 className="w-5 h-5" />
                                        You are logged in as: {user?.email}
                                    </p>
                                </div>
                                <p className="text-muted-foreground">
                                    Click the button below to permanently delete your account and all associated data.
                                </p>
                                <Button 
                                    variant="destructive" 
                                    size="lg"
                                    onClick={() => setShowDeleteDialog(true)}
                                    className="w-full sm:w-auto"
                                    data-testid="delete-account-btn"
                                >
                                    <Trash2 className="w-5 h-5 mr-2" />
                                    Delete My Account
                                </Button>
                            </div>
                        ) : (
                            /* Not logged in - Instructions */
                            <div className="space-y-6">
                                <div className="space-y-4">
                                    <h3 className="font-semibold text-lg">Option 1: Delete via App</h3>
                                    <ol className="space-y-3 list-decimal list-inside text-muted-foreground">
                                        <li>Log in to your MediGuide account</li>
                                        <li>Go to Settings</li>
                                        <li>Scroll to "Danger Zone" and click "Delete Account"</li>
                                        <li>Type DELETE to confirm and click the delete button</li>
                                    </ol>
                                    <Link to="/login">
                                        <Button className="mt-2" data-testid="login-to-delete-btn">
                                            Log In to Delete Account
                                        </Button>
                                    </Link>
                                </div>

                                <div className="border-t pt-6">
                                    <h3 className="font-semibold text-lg">Option 2: Request via Email</h3>
                                    <p className="text-muted-foreground mt-2 mb-4">
                                        If you cannot access your account, send an email to our support team with the subject "Account Deletion Request" from the email address associated with your account.
                                    </p>
                                    <a href="mailto:support@mediguide.online?subject=Account%20Deletion%20Request&body=Please%20delete%20my%20MediGuide%20account%20and%20all%20associated%20data.%0A%0AEmail%20address%20on%20account%3A%20%5BYour%20email%5D%0A%0AI%20understand%20this%20action%20is%20permanent%20and%20cannot%20be%20undone.">
                                        <Button variant="outline" data-testid="email-deletion-btn">
                                            <Mail className="w-4 h-4 mr-2" />
                                            Send Deletion Request Email
                                        </Button>
                                    </a>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Data Retention Info */}
                <Card className="mb-8">
                    <CardHeader>
                        <CardTitle>Data Retention Policy</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-muted-foreground">
                        <p>
                            When you delete your account, we immediately begin the deletion process. Your data will be permanently removed from our systems within 30 days.
                        </p>
                        <p>
                            Some anonymized, aggregated data may be retained for analytics purposes, but this data cannot be linked back to you in any way.
                        </p>
                        <p>
                            If required by law, certain records may be retained for legal compliance purposes.
                        </p>
                    </CardContent>
                </Card>

                {/* Contact */}
                <div className="text-center text-muted-foreground">
                    <p>Questions about data deletion?</p>
                    <a href="mailto:support@mediguide.online" className="text-primary hover:underline">
                        support@mediguide.online
                    </a>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t bg-card py-6 mt-12">
                <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <Activity className="w-5 h-5 text-primary" />
                        <span className="font-heading font-semibold">MediGuide AI</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-muted-foreground">
                        <Link to="/privacy" className="hover:text-primary transition-colors">
                            Privacy Policy
                        </Link>
                        <Link to="/terms" className="hover:text-primary transition-colors">
                            Terms of Service
                        </Link>
                    </div>
                </div>
            </footer>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Confirm Account Deletion
                        </DialogTitle>
                        <DialogDescription>
                            This action cannot be undone. All your data will be permanently deleted.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                            Type <span className="font-bold text-red-600">DELETE</span> to confirm:
                        </p>
                        <Input
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="DELETE"
                            className="border-red-300 focus:border-red-500"
                            data-testid="delete-confirm-input"
                        />
                    </div>
                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setShowDeleteDialog(false);
                                setConfirmText('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="destructive"
                            onClick={handleDeleteAccount}
                            disabled={confirmText !== 'DELETE' || deleting}
                            data-testid="confirm-delete-btn"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Permanently Delete
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
